import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { Textarea } from "@/components/core/forms/textarea";
import { useUpdateCollectionStatusMutation } from "@/features/dashboard/api/queries/contract-invoice-query";
import type { ContractInvoice } from "@/features/dashboard/types/contract-invoice";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCalendar, IconCircleCheck, IconCircleHalf2, IconClock, IconCurrencyPeso, IconReceipt, IconUser } from "@tabler/icons-react";
import { format } from "date-fns";
import type React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const invoiceCollectionStatusSchema = z.object({
	collection_status: z.enum(["pending", "partial", "collected"]),
	collection_remarks: z.string().optional(),
	date_collected: z.string().optional(),
	collected_by: z.string().optional(),
	collection_receipt: z.string().optional(),
	collection_amount: z.union([z.string(), z.number()]).optional(),
});

type InvoiceCollectionStatusFormData = z.infer<typeof invoiceCollectionStatusSchema>;

interface InvoiceCollectionStatusModalProps {
	invoice: ContractInvoice;
	onSuccess?: () => void;
}

export const InvoiceCollectionStatusModal: React.FC<InvoiceCollectionStatusModalProps> = ({ invoice, onSuccess }) => {
	const { user } = useAuth();
	const updateMutation = useUpdateCollectionStatusMutation();

	const isFullyCollected = invoice.collection_status === "collected";
	const amountCollected = invoice.amount_collected ?? 0;
	const remainingBalance = (invoice.amount ?? 0) - amountCollected;

	const defaultStatus = isFullyCollected ? "pending" : invoice.collection_status === "partial" ? "partial" : "collected";

	const form = useForm<InvoiceCollectionStatusFormData>({
		resolver: zodResolver(invoiceCollectionStatusSchema),
		defaultValues: {
			collection_status: defaultStatus,
			collection_remarks: invoice.collection_remarks || "",
			date_collected: invoice.date_collected || format(new Date(), "yyyy-MM-dd"),
			collected_by: invoice.collected_by?.name || user?.name || `${user?.first_name} ${user?.last_name}` || "",
			collection_receipt: invoice.collection_receipt || "",
			collection_amount: "",
		},
	});

	const watchedStatus = form.watch("collection_status");

	const isPaymentStatus = (status: string) => status === "collected" || status === "partial";

	const onSubmit = async (data: InvoiceCollectionStatusFormData) => {
		try {
			await updateMutation.mutateAsync({
				invoiceId: invoice.id,
				data: {
					collection_status: isPaymentStatus(data.collection_status) ? "collected" : "pending",
					collection_remarks: data.collection_remarks || undefined,
					date_collected: isPaymentStatus(data.collection_status) ? data.date_collected : undefined,
					collected_by: isPaymentStatus(data.collection_status) ? data.collected_by : undefined,
					collection_receipt: isPaymentStatus(data.collection_status) ? data.collection_receipt : undefined,
					collection_amount: isPaymentStatus(data.collection_status) ? Number(data.collection_amount) : undefined,
				},
			});

			onSuccess?.();
		} catch (error) {
			// Error handled by mutation
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
				<div className="flex flex-col w-full gap-6">
					{/* Invoice Details Header */}
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<h3 className="font-medium text-blue-900 mb-2">Invoice Details</h3>
						<div className="text-sm text-blue-800">
							<p>
								<strong>Invoice Number:</strong> {invoice.invoice_number}
							</p>
							<p>
								<strong>Amount:</strong> ₱{formatNumber(invoice.amount)}
							</p>
							{amountCollected > 0 && (
								<>
									<p className="text-green-700">
										<strong>Collected:</strong> ₱{formatNumber(amountCollected)}
									</p>
									<p className="text-red-700 font-semibold">
										<strong>Remaining Balance:</strong> ₱{formatNumber(remainingBalance)}
									</p>
								</>
							)}
							<p>
								<strong>Description:</strong> {invoice.description || "Invoice"}
							</p>
							<p>
								<strong>Due Date:</strong> {formatDate(invoice.due_date, "PP")}
							</p>
						</div>
					</div>

					{/* Status Selection */}
					<FormField
						control={form.control}
						name="collection_status"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Collection Status</FormLabel>
								<FormGroup>
									<div className="grid grid-cols-3 gap-3">
										<button
											type="button"
											onClick={() => field.onChange("collected")}
											className={`p-3 rounded-lg border-2 transition-all ${
												field.value === "collected" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="flex items-center justify-center gap-2">
												<IconCircleCheck className="w-5 h-5" />
												<span className="font-medium">Collected</span>
											</div>
										</button>
										<button
											type="button"
											onClick={() => field.onChange("partial")}
											className={`p-3 rounded-lg border-2 transition-all ${
												field.value === "partial" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="flex items-center justify-center gap-2">
												<IconCircleHalf2 className="w-5 h-5" />
												<span className="font-medium">Partial</span>
											</div>
										</button>
										<button
											type="button"
											onClick={() => field.onChange("pending")}
											className={`p-3 rounded-lg border-2 transition-all ${
												field.value === "pending" ? "border-yellow-500 bg-yellow-50 text-yellow-700" : "border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="flex items-center justify-center gap-2">
												<IconClock className="w-5 h-5" />
												<span className="font-medium">Pending</span>
											</div>
										</button>
									</div>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Collection Details - Show when marking as collected or partial */}
					{isPaymentStatus(watchedStatus) && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6">
								<FormField
									control={form.control}
									name="date_collected"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												<IconCalendar className="inline w-4 h-4 mr-1" />
												Date Collected
											</FormLabel>
											<FormGroup>
												<Input className="input" type="date" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-6">
								<FormField
									control={form.control}
									name="collected_by"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												<IconUser className="inline w-4 h-4 mr-1" />
												Collected By
											</FormLabel>
											<FormGroup>
												<Input className="input" {...field} placeholder="Name of person who collected" />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-12">
								<FormField
									control={form.control}
									name="collection_amount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												<IconCurrencyPeso className="inline w-4 h-4 mr-1" />
												Amount to Collect
											</FormLabel>
											<FormGroup>
												<Input
													className="input"
													type="number"
													step="0.01"
													min="0.01"
													max={remainingBalance}
													{...field}
													placeholder={`Max: ₱${formatNumber(remainingBalance)}`}
												/>
											</FormGroup>
											<p className="text-xs text-gray-500 mt-1">Remaining balance: ₱{formatNumber(remainingBalance)}</p>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-12">
								<FormField
									control={form.control}
									name="collection_receipt"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<IconReceipt className="inline w-4 h-4 mr-1" />
												Collection Receipt Number
											</FormLabel>
											<FormGroup>
												<Input className="input" {...field} placeholder="Enter receipt number (optional)" />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}

					{/* Remarks - Always show */}
					<FormField
						control={form.control}
						name="collection_remarks"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Remarks {watchedStatus === "pending" && "(e.g., reason for pending)"}</FormLabel>
								<FormGroup>
									<Textarea
										className="input"
										{...field}
										placeholder={isPaymentStatus(watchedStatus) ? "Add any notes about the collection..." : "Add reason why collection is pending..."}
										rows={3}
									/>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Current Collection Info if exists */}
					{invoice.collection_status !== "pending" && invoice.date_collected && (
						<div className="bg-blue-50 p-3 rounded-lg text-sm">
							<p className="font-medium text-blue-900 mb-1">Previous Collection Info:</p>
							<p className="text-blue-700">
								Collected on {formatDate(invoice.date_collected, "PP")} by {invoice.collected_by?.name}
							</p>
							{invoice.collection_receipt && <p className="text-blue-700">Receipt: {invoice.collection_receipt}</p>}
							{invoice.collection_remarks && <p className="text-blue-600 mt-1 italic">"{invoice.collection_remarks}"</p>}
						</div>
					)}
				</div>

				<div className="flex justify-end gap-2 pt-4 border-t">
					<Button type="button" variant="outline" onClick={onSuccess}>
						Cancel
					</Button>
					<Button type="submit" disabled={updateMutation.isPending} className={isPaymentStatus(watchedStatus) ? "bg-green-600 hover:bg-green-700" : ""}>
						{updateMutation.isPending
							? "Updating..."
							: watchedStatus === "collected"
								? "Mark as Collected"
								: watchedStatus === "partial"
									? "Record Partial Payment"
									: "Mark as Pending"}
					</Button>
				</div>
			</form>
		</Form>
	);
};
